// Helpers to read/write per-symptom severity to Supabase `daily_logs`.
import { supabase } from "@/integrations/supabase/client";


export type DailyLogEntry = {
  log_date: string;       // YYYY-MM-DD
  symptom_name: string;
  severity: number;       // 0=not logged, 1..4
  count: number;          // event count for the day (vasomotor uses this; others stay at 1)
};

// YYYY-MM-DD in local time (so "today" matches what the user sees on the device).
export function localDateKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Parse a YYYY-MM-DD date-only string as a local-TZ Date (midnight local).
// Returns null for empty/invalid input. Use this for any math against
// hrtStartDate / daily_hrt.log_date / periods.start_date so we avoid the
// UTC drift you get from `new Date("YYYY-MM-DD")`.
export function parseLocalDateKey(s: string | null | undefined): Date | null {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

// Build an array of the last `n` local dates (oldest first), ending today.
export function lastNDates(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(localDateKey(d));
  }
  return out;
}

// ISO timestamp for local-midnight (00:00:00) at the START of `dateKey`,
// in the user's local timezone. Used to bracket [start, nextDayStart) windows
// when querying symptom_events (which stores occurred_at as timestamptz).
// Falls back to "now" if dateKey is malformed (matches parseLocalDateKey).
export function localMidnightISO(dateKey: string): string {
  const d = parseLocalDateKey(dateKey);
  return (d ?? new Date()).toISOString();
}

// YYYY-MM-DD for the day AFTER `dateKey`, computed in local TZ so it lines
// up with localDateKey()/localMidnightISO() across DST transitions.
export function nextDayKey(dateKey: string): string {
  const d = parseLocalDateKey(dateKey);
  if (!d) return dateKey;
  d.setDate(d.getDate() + 1);
  return localDateKey(d);
}

// Replace today's full set of symptom rows for this user.
//  - Symptoms in `entries` are upserted (severity defaults to 2 if not provided).
//  - Symptoms NOT in `entries` for today are deleted (so unchecking removes them).
export async function replaceTodayLogs(
  userId: string,
  entries: { symptom_name: string; severity?: number }[],
  dateKey: string = localDateKey(),
): Promise<void> {
  // Defense-in-depth: event-based symptoms (hot flashes) are never written
  // through this severity-upsert path — they own daily_logs.count via
  // quickLogSymptom and severity stays NULL post-Turn-2e cleanup. Filtering
  // here means the check-in card + past-day edit panel cannot accidentally
  // re-introduce a stale severity for an event symptom.
  const rows = entries
    .filter(e => !isEventSymptom(e.symptom_name))
    .map(e => ({
      user_id: userId,
      log_date: dateKey,
      symptom_name: e.symptom_name,
      severity: typeof e.severity === "number" ? Math.max(0, Math.min(4, e.severity)) : 2,
    }));

  // 1. Upsert active symptoms.
  if (rows.length > 0) {
    const { error: upErr } = await supabase
      .from("daily_logs")
      .upsert(rows, { onConflict: "user_id,log_date,symptom_name" });
    if (upErr) throw upErr;
  }

  // 2. Delete any symptom rows for today that the user no longer has selected.
  //    Event-based symptoms (currently just Hot flashes) are NEVER touched
  //    here: each tap creates a symptom_events row and bumps daily_logs.count
  //    as a denorm cache. The unified symptom card surfaces a "+ Hot Flash"
  //    chip whose count is independent of the once-a-day severity entries,
  //    so a save from the check-in card must not silently wipe quick-logged
  //    counts — unconditional exclusion via
  //    .not("symptom_name", "in", EVENT_SYMPTOMS). The legacy `count > 1`
  //    guard remains as a belt-and-suspenders defense against any future
  //    event-based symptom we forget to add to the list.
  const keep = rows.map(r => r.symptom_name);
  const vasoIn = `(${EVENT_SYMPTOMS.map(s => `"${s.replace(/"/g, '""')}"`).join(",")})`;
  let q = supabase
    .from("daily_logs")
    .delete()
    .eq("user_id", userId)
    .eq("log_date", dateKey)
    .lte("count", 1)
    .not("symptom_name", "in", vasoIn);
  if (keep.length > 0) {
    q = q.not("symptom_name", "in", `(${keep.map(s => `"${s.replace(/"/g, '""')}"`).join(",")})`);
  }
  const { error: delErr } = await q;
  if (delErr) throw delErr;
}

// Vasomotor symptoms that participate in the event-level model (chip count +
// time-of-day strip + symptom_events source-of-truth). Other symptoms keep
// the severity-only model and never touch symptom_events.
// Event-based symptoms participate in the event-level model (tap-to-count +
// symptom_events source-of-truth). Currently only Hot flashes — night sweats
// reverted to the severity-only model because users can't tap a button
// during sleep. Other symptoms keep severity-only and never touch
// symptom_events.
export const EVENT_SYMPTOMS = ["Hot flashes"] as const;
export type EventSymptom = typeof EVENT_SYMPTOMS[number];
export function isEventSymptom(name: string): name is EventSymptom {
  return (EVENT_SYMPTOMS as readonly string[]).includes(name);
}

export type SymptomEvent = {
  id: string;
  symptom_name: string;
  occurred_at: string; // ISO timestamptz
  severity: number;
};

// Turn 2b.4 past-day-edit aliases. Same bodies as the Today helpers, but
// dateKey is required — caller must pass an explicit YYYY-MM-DD so we can
// statically distinguish today vs historical edits at the call site.
export function fetchVasomotorEventsForDate(userId: string, dateKey: string) {
  return fetchTodayVasomotorEvents(userId, dateKey);
}
export function replacePastDayLogs(
  userId: string,
  dateKey: string,
  entries: { symptom_name: string; severity?: number }[],
) {
  return replaceTodayLogs(userId, entries, dateKey);
}

// Quick-log a single symptom EVENT for `dateKey` (local-TZ today by default).
//
// Hybrid model (vasomotor-first):
//   - symptom_events: source of truth. One row INSERTed per tap with
//     occurred_at = now(). Drives the time-of-day strip, insights charts,
//     and "% reduction in daily hot flash count" in the Symptom Intelligence Report.
//   - daily_logs: denormalized cache. count is UPDATEd to LEAST(50, count+1)
//     on each tap so chip labels and the heatmap can render instantly from a
//     single per-day row without aggregating events client-side.
//
// Severity is NOT bumped on subsequent taps — severity = how bad an episode
// felt, count = how many episodes happened. Independent clinical axes.
//
// Returns { count, severity, isFirstOfDay, eventId, occurredAt } so the
// caller can pick toast wording and reconcile the optimistic strip update.
//
// v1.1 hardening note: the daily_logs UPDATE is still a fetch -> check ->
// write pattern and is NOT race-safe under rapid double-taps. The event row
// always lands correctly (each tap = one INSERT); only the cached count can
// drift by 1 in pathological cases. Long-term fix: Postgres RPC with
// INSERT ... ON CONFLICT DO UPDATE SET count = LEAST(50, daily_logs.count + 1).
export async function quickLogSymptom(
  userId: string,
  symptomName: string,
  dateKey: string = localDateKey(),
): Promise<{ count: number; severity: number; isFirstOfDay: boolean; eventId: string; occurredAt: string }> {
  // Atomic server-side write: symptom_events INSERT + daily_logs UPSERT run in
  // a single RPC transaction (public.quick_log_symptom, SECURITY INVOKER, RLS
  // applies). Replaces the prior client-side fetch/check/insert sequence,
  // which was not race-safe under rapid double-taps.
  const { data, error } = await supabase.rpc("quick_log_symptom", {
    p_symptom_name: symptomName,
    p_severity: 2,
    p_log_date: dateKey,
  });
  if (error) throw error;
  const row: any = Array.isArray(data) ? data[0] : data;
  return {
    count: Number(row?.new_count) || 1,
    severity: Number(row?.effective_severity) || 0,
    isFirstOfDay: Boolean(row?.is_first_of_day),
    eventId: String(row?.event_id ?? ""),
    occurredAt: String(row?.occurred_at ?? new Date().toISOString()),
  };
}


// Delete a single symptom_events row by id, then decrement the matching
// daily_logs.count cache (floor at 0). RLS scopes the delete to the
// current user. Used by the Hot Flash Manager bottom sheet so users can
// undo accidental taps without leaving the home screen.
export async function deleteSymptomEvent(eventId: string): Promise<void> {
  // Read the event first so we can decrement the right daily_logs row.
  const { data: ev, error: selErr } = await supabase
    .from("symptom_events")
    .select("user_id, symptom_name, occurred_at")
    .eq("id", eventId)
    .maybeSingle();
  if (selErr) throw selErr;
  const { error: delErr } = await supabase
    .from("symptom_events")
    .delete()
    .eq("id", eventId);
  if (delErr) throw delErr;
  if (!ev) return;
  // Recompute the daily_logs.count from symptom_events for that user/day
  // so cache stays consistent even if multiple deletes race.
  const dk = localDateKey(new Date((ev as any).occurred_at));
  const start = parseLocalDateKey(dk);
  if (!start) return;
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const { data: remaining } = await supabase
    .from("symptom_events")
    .select("id", { count: "exact", head: true })
    .eq("user_id", (ev as any).user_id)
    .eq("symptom_name", (ev as any).symptom_name)
    .gte("occurred_at", start.toISOString())
    .lt("occurred_at", end.toISOString());
  const newCount = (remaining as any)?.length ?? 0; // .select head:true returns null data; rely on response.count below
  // The PostgREST count comes back on the response object; fall back to a
  // second tiny query if we can't read it inline.
  let finalCount = newCount;
  try {
    const r = await supabase
      .from("symptom_events")
      .select("id", { count: "exact" })
      .eq("user_id", (ev as any).user_id)
      .eq("symptom_name", (ev as any).symptom_name)
      .gte("occurred_at", start.toISOString())
      .lt("occurred_at", end.toISOString())
      .limit(1);
    finalCount = (r.count ?? 0);
  } catch { /* keep newCount */ }
  await supabase
    .from("daily_logs")
    .update({ count: finalCount })
    .eq("user_id", (ev as any).user_id)
    .eq("log_date", dk)
    .eq("symptom_name", (ev as any).symptom_name);
}

// Fetch today's vasomotor events for the time-of-day strip.
// Source of truth = symptom_events. Returns events ordered oldest -> newest.
export async function fetchTodayVasomotorEvents(
  userId: string,
  dateKey: string = localDateKey(),
): Promise<SymptomEvent[]> {
  // Convert the local-TZ date key into a [start, end) UTC window so the
  // server can use the (user_id, occurred_at DESC) index. We bracket
  // generously (local midnight..next local midnight) by constructing Date
  // from the YYYY-MM-DD via parseLocalDateKey.
  const start = parseLocalDateKey(dateKey);
  if (!start) return [];
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const { data, error } = await supabase
    .from("symptom_events")
    .select("id, symptom_name, occurred_at, severity")
    .eq("user_id", userId)
    .in("symptom_name", EVENT_SYMPTOMS as unknown as string[])
    .gte("occurred_at", start.toISOString())
    .lt("occurred_at", end.toISOString())
    .order("occurred_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.id as string,
    symptom_name: r.symptom_name as string,
    occurred_at: r.occurred_at as string,
    severity: r.severity as number,
  }));
}

// Fetch every Hot flash event for [startDateKey, endDateKey] (inclusive of
// both local-TZ days). Used by the Symptom Intelligence Report event section + per-event
// Direction comparison. Returns oldest -> newest.
export async function fetchHotFlashEventsBetween(
  userId: string,
  startDateKey: string,
  endDateKey: string,
): Promise<SymptomEvent[]> {
  const start = parseLocalDateKey(startDateKey);
  const endStart = parseLocalDateKey(endDateKey);
  if (!start || !endStart) return [];
  const end = new Date(endStart); end.setDate(end.getDate() + 1);
  const { data, error } = await supabase
    .from("symptom_events")
    .select("id, symptom_name, occurred_at, severity")
    .eq("user_id", userId)
    .in("symptom_name", EVENT_SYMPTOMS as unknown as string[])
    .gte("occurred_at", start.toISOString())
    .lt("occurred_at", end.toISOString())
    .order("occurred_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.id as string,
    symptom_name: r.symptom_name as string,
    occurred_at: r.occurred_at as string,
    severity: r.severity as number,
  }));
}

// Client-side timezone self-correction. Reads the browser's IANA TZ via
// Intl, compares against profiles.timezone, and writes back if different.
// Cheap, idempotent, fire-and-forget. Errors are swallowed (best-effort).
export async function syncProfileTimezone(userId: string): Promise<void> {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected || detected.length > 64) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("timezone")
      .eq("id", userId)
      .maybeSingle();
    if (error || !data) return;
    if ((data as any).timezone === detected) return;
    await supabase.from("profiles").update({ timezone: detected }).eq("id", userId);
  } catch {
    /* best-effort */
  }
}

// Upsert today's mood (0..4) into daily_moods. Pass null to clear.
export async function saveTodayMood(
  userId: string,
  mood: number | null,
  dateKey: string = localDateKey(),
): Promise<void> {
  if (mood === null) {
    const { error } = await supabase
      .from("daily_moods")
      .delete()
      .eq("user_id", userId)
      .eq("log_date", dateKey);
    if (error) throw error;
    return;
  }
  const clamped = Math.max(0, Math.min(4, Math.round(mood)));
  const { error } = await supabase
    .from("daily_moods")
    .upsert(
      { user_id: userId, log_date: dateKey, mood: clamped },
      { onConflict: "user_id,log_date" },
    );
  if (error) throw error;
}

// Pull all daily_moods rows for the user in the given date window (inclusive).
// Returns [] when no rows exist — callers should treat missing dates as "no mood logged".
export async function fetchMoodsBetween(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<{ log_date: string; mood: number }[]> {
  const { data, error } = await supabase
    .from("daily_moods")
    .select("log_date, mood")
    .eq("user_id", userId)
    .gte("log_date", startDate)
    .lte("log_date", endDate);
  if (error) throw error;
  return (data || []).map(r => ({ log_date: r.log_date as string, mood: r.mood as number }));
}

// Upsert today's HRT-taken flag into daily_hrt. Pass null to clear (delete the row).
export async function saveTodayHrt(
  userId: string,
  hrtTaken: boolean | null,
  dateKey: string = localDateKey(),
): Promise<void> {
  if (hrtTaken === null) {
    const { error } = await supabase
      .from("daily_hrt")
      .delete()
      .eq("user_id", userId)
      .eq("log_date", dateKey);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("daily_hrt")
    .upsert(
      { user_id: userId, log_date: dateKey, hrt_taken: hrtTaken },
      { onConflict: "user_id,log_date" },
    );
  if (error) throw error;
}

// Returns [] when no rows. Callers treat missing dates as "no HRT logged".
export async function fetchHrtBetween(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<{ log_date: string; hrt_taken: boolean }[]> {
  const { data, error } = await supabase
    .from("daily_hrt")
    .select("log_date, hrt_taken")
    .eq("user_id", userId)
    .gte("log_date", startDate)
    .lte("log_date", endDate);
  if (error) throw error;
  return (data || []).map(r => ({
    log_date: r.log_date as string,
    hrt_taken: r.hrt_taken as boolean,
  }));
}

// Pull all daily_logs rows for the user in the given date window (inclusive).
export async function fetchLogsBetween(
  userId: string,
  startDate: string,
  endDate: string,
): Promise<DailyLogEntry[]> {
  const { data, error } = await supabase
    .from("daily_logs")
    .select("log_date, symptom_name, severity, count")
    .eq("user_id", userId)
    .gte("log_date", startDate)
    .lte("log_date", endDate);
  if (error) throw error;
  return (data || []).map(r => ({
    log_date: r.log_date as string,
    symptom_name: r.symptom_name as string,
    severity: r.severity as number,
    count: (r as any).count as number,
  }));
}

// One-shot: migrate any existing localStorage `menotrack_today_*` entries into
// daily_logs. We don't know real severities for old logs, so we default to 2.
// Idempotent: upserts on the unique (user_id, log_date, symptom_name) key.
const MIGRATION_FLAG = "knowmeno_dailylogs_migrated_v1";

export async function migrateLocalStorageLogs(
  userId: string,
  symptomLabelByIndex: (i: number) => string | null,
): Promise<{ migrated: number }> {
  if (typeof window === "undefined") return { migrated: 0 };
  if (window.localStorage.getItem(MIGRATION_FLAG)) return { migrated: 0 };

  const rowsToUpsert: { user_id: string; log_date: string; symptom_name: string; severity: number }[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith("menotrack_today_")) continue;
    const dateStr = key.substring("menotrack_today_".length);
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) continue;
    const dateKey = localDateKey(parsed);

    let log: any;
    try { log = JSON.parse(window.localStorage.getItem(key) || "{}"); } catch { continue; }
    const syms: number[] = Array.isArray(log?.syms) ? log.syms : [];
    for (const idx of syms) {
      const name = symptomLabelByIndex(idx);
      if (!name) continue;
      rowsToUpsert.push({
        user_id: userId,
        log_date: dateKey,
        symptom_name: name,
        severity: 2,
      });
    }
  }

  if (rowsToUpsert.length > 0) {
    const { error } = await supabase
      .from("daily_logs")
      .upsert(rowsToUpsert, { onConflict: "user_id,log_date,symptom_name", ignoreDuplicates: true });
    if (error) {
      console.error("Daily logs migration failed", error);
      return { migrated: 0 };
    }
  }
  window.localStorage.setItem(MIGRATION_FLAG, new Date().toISOString());
  return { migrated: rowsToUpsert.length };
}
